import { BuilderContent } from "@builder.io/sdk";
import React from "react";

import { CATEGORY_LOCATIONS_CONTEXT } from "@/shared/constants";

import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import DefaultLayout from "@/layouts/default";

import { getBuilderMessages, getBuilderNavLinks } from "@/pages/api/builder/builder";
import { fetchCategory, getParentCategory } from "@/pages/api/store-directory/categories";
import { getFooterNavigation, getHeaderNavigation } from "@/pages/api/store-directory/navigation";

import { InStoreContainer } from "@/components/in-store/in-store-container";

import { CategoryProps } from "@/interfaces/category-interface";
import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

interface LocaleProps {
    locale: Locale;
}

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type OwnProps = {
    slug: string;
    builderMessages: Array<BuilderContent>;
    currentCategory: CategoryProps;
    parentCategory: CategoryProps;
    builderNavLinks: Array<BuilderContent>;
};

type Props = OwnProps & NavProps & LocaleProps;

type Params = {
    slug: string;
};

init(BuilderModel.PAGE);
init(BuilderModel.MESSAGE);

export const getServerSideProps: GetServerSideProps<Props, Params> = async (ctx) => {
    const { locale, resolvedUrl, params } = ctx;
    const slug = params.slug;

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        currentCategory: () => fetchCategory(locale, slug),
        builderNavLinks: () => getBuilderNavLinks(locale),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    const parentCategory = await getParentCategory(locale, parallelProps.currentCategory?.parentId);

    return {
        props: {
            locale,
            slug,
            parentCategory,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const CategoryLocations = ({
    locale,
    slug,
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
    builderMessages,
    currentCategory,
    parentCategory,
    builderNavLinks,
}: Props): React.ReactNode => {
    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={builderNavLinks}
            builderMessages={builderMessages}
        >
            <InStoreContainer
                context={CATEGORY_LOCATIONS_CONTEXT}
                slug={slug}
                currentCategory={currentCategory}
                parentCategory={parentCategory}
            />
        </DefaultLayout>
    );
};

export default CategoryLocations;
